'use client';

import { Image } from '@repo/ui/Image';
import clsx from 'clsx';
import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { Icon } from '@/general/Icons/Icon';
import { boldMatchCharacters } from '@/general/helpers/boldMatchCharacters';
import { formatNumber } from '@/general/helpers/formatNumber';
import { parseType } from '@/general/helpers/fuzzySearchHelper';
import { SMART_SEARCH_PATH } from '@/general/helpers/searchDestination.helper';
import { useRouter } from '@/general/helpers/useRouter';
import { getFilter } from '@/hygiene/components/flexible-content/RecentSearch/RecentSearch.helper';
import { RecentFilterSearch } from '@/hygiene/components/flexible-content/RecentSearch/RecentSearch.type';
import { recentFilterSearchStore } from '@/stores/recentFilterSearchStore';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { ButtonIcon } from '../Button/ButtonIcon';
import { Link } from '../Link/Link';
import { Shade } from '../Shade/Shade';
import type { KeywordSearchResponseSection } from './KeywordSearch.types';

type Props = {
  section: KeywordSearchResponseSection;
  onResultClick?: () => void;
};

export const KeywordSearchResultsSection: FC<Props> = ({ section, onResultClick }) => {
  const router = useRouter();

  const onClickSuggestion = (item: { label: string; type: string; url: string }) => {
    onResultClick?.();
    pushToDataLayer({
      event: 'search_click_suggestion',
      value: item,
    });
    router.push(item.url);
  };

  const onClickRecentSearch = (item: { id: string; url: string }) => {
    onResultClick?.();
    pushToDataLayer({
      event: 'search_click_recent_search',
      value: item,
    });
    router.push(item.url);
  };

  const onClickPrompt = (label: string) => {
    onResultClick?.();
    pushToDataLayer({
      event: 'search_click_prompt',
      value: label,
    });
    router.push(`${SMART_SEARCH_PATH}?query=${encodeURIComponent(label)}`);
  };

  return (
    <section className="relative">
      <header className={clsx('mb-2', { 'flex items-center gap-1.5': section.type === 'prompts' })}>
        {section.type === 'prompts' && (
          <span className="text-for-pri-acc">
            <Icon name="ai-sparkle" size={16} />
          </span>
        )}
        <h3 className="c-fs-overline text-tex-def-tie3">{section.title}</h3>
      </header>
      {section.type === 'filters' && (
        <div className="flex flex-col" data-testid="keyword-search-results-filters">
          {section.items.map((item) => (
            <button
              key={item.label}
              className="c-shade_container c-shade_container--grey relative -mx-2 flex items-center justify-between overflow-hidden rounded-sm p-2 no-underline"
              onClick={() => onClickSuggestion(item)}
              data-type="navigation"
            >
              <Shade noBackground />
              <span className="c-fs-body1 text-tex-def-tie1" data-testid="keyword-search-results-filter-label">
                {boldMatchCharacters(item.label, item.matches)}
              </span>
              <span className="c-fs-body2 text-tex-def-tie2" data-testid="keyword-search-results-filter-type">
                {parseType(item.type)}
              </span>
            </button>
          ))}
        </div>
      )}
      {section.type === 'recentSearches' && (
        <div className="flex flex-col" data-testid="keyword-search-results-recentSearches">
          {section.items.map((item) => {
            const { title, caption } = getFilter(item);
            return (
              <div key={item.id} className="relative -mx-2">
                <button
                  className="c-shade_container c-shade_container--grey relative box-border block w-full cursor-pointer overflow-hidden rounded-sm p-2 text-left no-underline"
                  onClick={() => onClickRecentSearch(item)}
                  data-type="navigation"
                >
                  <Shade noBackground />
                  <span className="block w-full">
                    <span className="c-fs-body1 block font-extrabold text-tex-def-tie1">{title}</span>
                    {!!caption && <span className="c-fs-body2 block text-tex-def-tie2">{caption}</span>}
                  </span>
                </button>
                <div className="absolute top-1/2 right-2 -translate-y-1/2">
                  <RemoveRecentSearch recentSearch={item} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {section.type === 'cars' && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="keyword-search-results-cars">
          {section.items.map((item, index) => (
            <Link
              key={item.sku}
              className={clsx(
                'radius-sm c-shade_container c-shade_container--grey relative block overflow-hidden rounded-sm border border-bor-neu-bas no-underline shadow-elevation-2dp transition-shadow duration-200 hover:shadow-elevation-8dp',
                { 'hidden sm:block': index >= 2 },
              )}
              href={`/product-detail/${item.sku}`}
              onClick={() => onResultClick?.()}
              data-testid="keyword-search-result-car"
            >
              <Shade noBackground />
              <Image src={item.imageUrl} alt="" width={300} height={168} className="block h-auto w-full" />
              <div className="p-2">
                <div
                  className="c-fs-body2 font-extrabold text-tex-def-tie1"
                  data-testid="keyword-search-results-car-model"
                >
                  {item.model}
                </div>
                <div className="c-fs-caption2 text-tex-def-tie2">
                  {formatNumber(item.kilometers, { append: ' km' })}
                </div>
                <div className="c-fs-body2 font-extrabold text-tex-def-tie1">
                  {formatNumber(item.price, { prepend: '$' })}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {section.type === 'prompts' && (
        <div className="flex flex-col" data-testid="keyword-search-results-prompts">
          {section.items.map((item) => (
            <button
              key={item.label}
              type="button"
              className="c-shade_container c-shade_container--grey relative -mx-2 flex cursor-pointer items-center gap-3 overflow-hidden rounded-sm p-2 text-left no-underline"
              onClick={() => onClickPrompt(item.label)}
              data-type="navigation"
              data-testid="keyword-search-results-prompt"
            >
              <Shade noBackground />
              <span className="c-fs-body1 text-tex-def-tie1">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

const RemoveRecentSearch: FC<{ recentSearch: RecentFilterSearch }> = ({ recentSearch }) => {
  const { removeFilterSearch } = recentFilterSearchStore(
    (state) => ({
      removeFilterSearch: state.removeFilterSearch,
    }),
    shallow,
  );
  return (
    <ButtonIcon
      icon={<Icon name="trash" />}
      title="Remove"
      variant="tertiary"
      styling="round"
      size="xsmall"
      onClick={() => removeFilterSearch(recentSearch)}
    />
  );
};
